import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Radio,
  GitBranch,
  Copy,
  Check,
  Terminal,
  Database,
  FileSpreadsheet,
  Zap,
  Shield,
  BarChart3,
  BookOpen,
  ArrowRight,
  ChevronRight,
  Package,
  Key,
  Play,
  CheckCircle2,
  Settings,
} from 'lucide-react';

/* ─── Animation ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

/* ─── Code snippets ─── */

const CONFIG_CODE = `import json
from Artifi_ge_lib import consumer as pdqlib

# Load PDQ configuration
with open("pdq_config.json", "r") as f:
    config = json.load(f)

# Initialize the PDQ Consumer
pdq = pdqlib.PDQConsumer(config)`;

const CONFIG_JSON = `{
    "consumer_api": "https://your-dq-api.example.com/api",
    "client_id": "your_okta_client_id",
    "secret": "your_okta_secret",
    "okta_url": "your-org.okta.com",
    "auth_server": "default",
    "scope": "exec"
}`;

const PANDAS_CODE = `import pandas as pd
from Artifi_ge_lib import consumer as pdqlib
from Artifi_ge_lib import common as pdq_utils

# 1. Initialize consumer
pdq = pdqlib.PDQConsumer(config)

# 2. Load your data
df = pd.read_csv("customers.csv")
# Or from any source: pd.read_parquet(), pd.read_sql(), etc.

# 3. Run validation + auto-profiling
results = pdq.run(
    asset_data=df,
    asset_name="customers",
    project_name="Customer_DQ",
    group_name="Quality_Rules_V1",
)

# 4. Check results
print(f"Success: {results.success}")
print(f"Stats:   {results.statistics}")

# 5. Handle failures by severity
if not results.success:
    failures = results.get_failures_by_severity()
    for severity in ["High", "Medium", "Low"]:
        for f in failures.get(severity, []):
            print(f"  [{severity}] {f.expectation_config.type}")

# 6. Access profiling metrics
profiling = results.meta.get("profiling_metrics")
for col in profiling.get("columns", []):
    print(f"  {col['column_name']}: nulls={col['null_percent']}%")

# 7. Generate HTML reports
html = pdq_utils.generate_profiling_html(profiling)
with open("profiling_report.html", "w") as f:
    f.write(html)`;

const SPARK_CODE = `from pyspark.sql import SparkSession
from Artifi_ge_lib import consumer as pdqlib
from Artifi_ge_lib import common as pdq_utils

# 1. Initialize consumer
pdq = pdqlib.PDQConsumer(config)

# 2. Load your Spark DataFrame
spark = SparkSession.builder.appName("DQ").getOrCreate()
spark_df = spark.read.parquet("s3://bucket/data/transactions/")
# Or: spark.read.csv(), spark.table(), spark.sql(), etc.

# 3. Run validation + auto-profiling
results = pdq.run(
    asset_data=spark_df,
    asset_name="transactions",
    project_name="Finance_DQ",
    group_name="Transaction_Rules_V1",
)

# 4. Check results
print(f"Success: {results.success}")
print(f"Stats:   {results.statistics}")

# 5. Quality gate — fail the pipeline if DQ fails
if not results.success:
    failures = results.get_failures_by_severity()
    if failures.get("High"):
        raise Exception(
            f"DQ GATE FAILED: {len(failures['High'])} high-severity issues"
        )

# 6. Access profiling metrics
profiling = results.meta.get("profiling_metrics")
print(f"Row count: {profiling['row_count']}")
print(f"Columns:   {profiling['column_count']}")`;

const SQL_CODE = `import json
from urllib.parse import quote_plus
from Artifi_ge_lib import consumer as pdqlib
from Artifi_ge_lib import common as pdq_utils

# 1. Initialize consumer
pdq = pdqlib.PDQConsumer(config)

# 2. Build connection string
SNOWFLAKE_USER     = "your_user"
SNOWFLAKE_PASSWORD = "your_password"
SNOWFLAKE_ACCOUNT  = "your_account"
SNOWFLAKE_DATABASE = "DQ_TEST"
SNOWFLAKE_SCHEMA   = "BRONZE"
SNOWFLAKE_WAREHOUSE = "COMPUTE_WH"

encoded_password = quote_plus(SNOWFLAKE_PASSWORD)
connection_string = (
    f"snowflake://{SNOWFLAKE_USER}:{encoded_password}"
    f"@{SNOWFLAKE_ACCOUNT}/{SNOWFLAKE_DATABASE}/{SNOWFLAKE_SCHEMA}"
    f"?warehouse={SNOWFLAKE_WAREHOUSE}&role=ACCOUNTADMIN"
)

# 3. Configure checkpoint (optional)
checkpoint_config = {
    "name": "SimpleBatchCheckpoint",
    "runtime_configuration": {
        "result_format": {
            "result_format": "COMPLETE",
            "include_unexpected_rows": True,
            "partial_unexpected_count": 20,
        }
    }
}

# 4. Run validation + auto-profiling on a table
results = pdq.run(
    asset_data="POLICY_TERM_DETAILS",
    asset_name="POLICY_TERM_DETAILS",
    project_name="Insurance_DQ",
    group_name="Policy_Rules_V1",
    datasource_config="SQL",
    connection_string=connection_string,
    checkpoint=checkpoint_config,
)

# 5. Check results
print(f"Success: {results.success}")
print(f"Stats:   {results.statistics}")

# 6. Access profiling metrics (auto-generated)
profiling = results.meta.get("profiling_metrics")
print(f"Row count:    {profiling['row_count']}")
print(f"Column count: {profiling['column_count']}")

for col in profiling.get("columns", []):
    print(f"  {col['column_name']:<25} "
          f"type={col['type']:<12} "
          f"nulls={col['null_percent']}% "
          f"min={col.get('min', '-')} "
          f"max={col.get('max', '-')}")

# 7. Profiling rules (freshness, outliers, constants)
for rule in profiling.get("rule_results", []):
    status = "PASS" if rule["success"] else "FAIL"
    print(f"  [{status}] {rule['rule']} on {rule.get('column', 'N/A')}")

# 8. Generate HTML reports
profiling_html = pdq_utils.generate_profiling_html(profiling)
with open("profiling_report.html", "w") as f:
    f.write(profiling_html)

validation_html = pdq_utils.generate_data_docs_html(results)
with open("validation_report.html", "w") as f:
    f.write(validation_html)`;

const POSTGRESQL_CONN = `# PostgreSQL
connection_string = "postgresql+psycopg2://user:pass@host:5432/database"

results = pdq.run(
    asset_data="public.customers",
    asset_name="customers",
    project_name="My_Project",
    group_name="My_Group",
    datasource_config="SQL",
    connection_string=connection_string,
)`;

const MYSQL_CONN = `# MySQL
connection_string = "mysql+pymysql://user:pass@host:3306/database"

results = pdq.run(
    asset_data="orders",
    asset_name="orders",
    project_name="My_Project",
    group_name="My_Group",
    datasource_config="SQL",
    connection_string=connection_string,
)`;

const DATABRICKS_CONN = `# Databricks
connection_string = (
    "databricks://token:your_token"
    "@your_workspace.azuredatabricks.net:443"
    "/default?http_path=/sql/1.0/warehouses/abc123"
)

results = pdq.run(
    asset_data="catalog.schema.table",
    asset_name="my_table",
    project_name="My_Project",
    group_name="My_Group",
    datasource_config="SQL",
    connection_string=connection_string,
)`;

/* ─── Runtime tab type ─── */
type RuntimeTab = 'pandas' | 'spark' | 'sql';
type ConnectorTab = 'snowflake' | 'postgresql' | 'mysql' | 'databricks';

const RUNTIME_TABS: { id: RuntimeTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'pandas', label: 'Pandas', icon: FileSpreadsheet },
  { id: 'spark', label: 'Spark', icon: Zap },
  { id: 'sql', label: 'SQL', icon: Database },
];

const CONNECTOR_TABS: { id: ConnectorTab; label: string }[] = [
  { id: 'snowflake', label: 'Snowflake' },
  { id: 'postgresql', label: 'PostgreSQL' },
  { id: 'mysql', label: 'MySQL' },
  { id: 'databricks', label: 'Databricks' },
];

const RUNTIME_CODE: Record<RuntimeTab, string> = {
  pandas: PANDAS_CODE,
  spark: SPARK_CODE,
  sql: SQL_CODE,
};

const CONNECTOR_CODE: Record<ConnectorTab, string> = {
  snowflake: SQL_CODE,
  postgresql: POSTGRESQL_CONN,
  mysql: MYSQL_CONN,
  databricks: DATABRICKS_CONN,
};

/* ─── Features ─── */
const features = [
  {
    icon: Shield,
    title: 'Quality Gates',
    description: 'Fail pipelines when critical data quality rules are violated. Prevent bad data from propagating downstream.',
  },
  {
    icon: BarChart3,
    title: 'Auto-Profiling',
    description: 'Every validation run auto-profiles your data — column stats, null rates, outlier detection, and freshness checks.',
  },
  {
    icon: GitBranch,
    title: 'Pipeline Integration',
    description: 'Drop into any Python pipeline — Airflow, Databricks, Glue, dbt, or custom ETL. Just import and run.',
  },
  {
    icon: BookOpen,
    title: '100+ Rules',
    description: 'Access the full DQ rule catalog. Add custom rules with the Producer API. Auto-update rules across all pipelines.',
  },
];

/* ─── How It Works Steps ─── */
const steps = [
  {
    step: '01',
    title: 'Define Rules',
    description: 'Use the SDQ wizard or Data Profiling to create projects, groups, and rules in the DQ Accelerator.',
  },
  {
    step: '02',
    title: 'Install SDK',
    description: 'Install Artifi_ge_lib in your pipeline environment. Configure authentication with your DQ API credentials.',
  },
  {
    step: '03',
    title: 'Embed in Pipeline',
    description: 'Import PDQConsumer, pass your DataFrame or table name, and call run(). The SDK handles everything else.',
  },
  {
    step: '04',
    title: 'Act on Results',
    description: 'Check pass/fail, get failures by severity, access profiling metrics, generate reports, or gate your pipeline.',
  },
];

/* ─── Copy button ─── */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors
                 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

/* ─── Python syntax highlighter ─── */
function highlightPython(code: string): React.ReactNode[] {
  const lines = code.split('\n');
  return lines.map((line, lineIdx) => {
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let keyIdx = 0;

    while (remaining.length > 0) {
      // Comments
      const commentMatch = remaining.match(/^(.*?)(#.*)$/);
      if (commentMatch && !remaining.match(/^["']/) && !remaining.match(/^.*["'].*#/)) {
        if (commentMatch[1]) {
          parts.push(...tokenizePython(commentMatch[1], `${lineIdx}-pre-${keyIdx++}`));
        }
        parts.push(
          <span key={`${lineIdx}-comment-${keyIdx++}`} className="text-[#8b949e] italic">
            {commentMatch[2]}
          </span>
        );
        remaining = '';
        break;
      }

      // Try to find comment after code (handle strings containing #)
      let inString = false;
      let stringChar = '';
      let commentStart = -1;
      for (let i = 0; i < remaining.length; i++) {
        const ch = remaining[i];
        if (!inString && (ch === '"' || ch === "'")) {
          // Check for triple quotes
          if (remaining.slice(i, i + 3) === '"""' || remaining.slice(i, i + 3) === "'''") {
            const closeIdx = remaining.indexOf(remaining.slice(i, i + 3), i + 3);
            if (closeIdx !== -1) {
              i = closeIdx + 2;
              continue;
            }
          }
          inString = true;
          stringChar = ch;
        } else if (inString && ch === stringChar && remaining[i - 1] !== '\\') {
          inString = false;
        } else if (!inString && ch === '#') {
          commentStart = i;
          break;
        }
      }

      if (commentStart !== -1) {
        parts.push(...tokenizePython(remaining.slice(0, commentStart), `${lineIdx}-code-${keyIdx++}`));
        parts.push(
          <span key={`${lineIdx}-comment2-${keyIdx++}`} className="text-[#8b949e] italic">
            {remaining.slice(commentStart)}
          </span>
        );
        remaining = '';
        break;
      }

      parts.push(...tokenizePython(remaining, `${lineIdx}-full-${keyIdx++}`));
      remaining = '';
    }

    return (
      <React.Fragment key={lineIdx}>
        {parts}
        {lineIdx < lines.length - 1 ? '\n' : ''}
      </React.Fragment>
    );
  });
}

const PY_KEYWORDS = new Set([
  'import', 'from', 'as', 'def', 'class', 'return', 'if', 'else', 'elif',
  'for', 'in', 'while', 'try', 'except', 'finally', 'with', 'raise',
  'not', 'and', 'or', 'is', 'None', 'True', 'False', 'pass', 'break',
  'continue', 'lambda', 'yield', 'assert', 'del', 'global', 'nonlocal',
]);

const PY_BUILTINS = new Set([
  'print', 'open', 'len', 'range', 'str', 'int', 'float', 'list', 'dict',
  'set', 'tuple', 'type', 'isinstance', 'round', 'Exception', 'json',
]);

function tokenizePython(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Regex to match: f-strings, triple-quoted strings, strings, numbers, keywords/identifiers, operators, other
  const tokenRegex = /(f"""[\s\S]*?"""|f'''[\s\S]*?'''|f"[^"]*"|f'[^']*'|"""[\s\S]*?"""|'''[\s\S]*?'''|"[^"]*"|'[^']*'|\b\d+\.?\d*\b|[a-zA-Z_]\w*|[^\s\w]|\s+)/g;
  let match;
  let idx = 0;

  while ((match = tokenRegex.exec(text)) !== null) {
    const token = match[0];
    const key = `${keyPrefix}-${idx++}`;

    // Strings (including f-strings)
    if (/^f?["']/.test(token)) {
      if (token.startsWith('f"') || token.startsWith("f'") || token.startsWith('f"""') || token.startsWith("f'''")) {
        // f-string: highlight f prefix differently
        parts.push(
          <span key={key} className="text-[#a5d6ff]">
            <span className="text-[#d2a8ff]">f</span>
            {token.slice(1)}
          </span>
        );
      } else {
        parts.push(
          <span key={key} className="text-[#a5d6ff]">{token}</span>
        );
      }
    }
    // Numbers
    else if (/^\d/.test(token)) {
      parts.push(
        <span key={key} className="text-[#79c0ff]">{token}</span>
      );
    }
    // Keywords
    else if (PY_KEYWORDS.has(token)) {
      parts.push(
        <span key={key} className="text-[#ff7b72]">{token}</span>
      );
    }
    // Builtins
    else if (PY_BUILTINS.has(token)) {
      parts.push(
        <span key={key} className="text-[#d2a8ff]">{token}</span>
      );
    }
    // Function calls (word followed by `(`)
    else if (/^[a-zA-Z_]\w*$/.test(token)) {
      const nextChar = text[tokenRegex.lastIndex];
      if (nextChar === '(') {
        parts.push(
          <span key={key} className="text-[#d2a8ff]">{token}</span>
        );
      } else {
        parts.push(
          <span key={key} className="text-[#e6edf3]">{token}</span>
        );
      }
    }
    // Operators and punctuation
    else if (/^[^\s\w]$/.test(token)) {
      parts.push(
        <span key={key} className="text-[#e6edf3]">{token}</span>
      );
    }
    // Whitespace / other
    else {
      parts.push(<span key={key}>{token}</span>);
    }
  }

  return parts;
}

/* ─── Code block ─── */
function CodeBlock({ code, title }: { code: string; title?: string }) {
  return (
    <div className="rounded-xl overflow-hidden border border-cargo-border bg-[#0d1117]">
      {title && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#161b22] border-b border-[#30363d]">
          <div className="flex items-center gap-2">
            <Terminal className="w-3.5 h-3.5 text-white/40" />
            <span className="text-xs font-medium text-white/60">{title}</span>
          </div>
          <CopyButton text={code} />
        </div>
      )}
      {!title && (
        <div className="flex justify-end px-4 py-2 bg-[#161b22] border-b border-[#30363d]">
          <CopyButton text={code} />
        </div>
      )}
      <pre className="p-4 overflow-x-auto text-sm leading-relaxed">
        <code className="font-mono text-[13px]">{highlightPython(code)}</code>
      </pre>
    </div>
  );
}

/* ─── Main Component ─── */
const PDQPage: React.FC = () => {
  const [activeRuntime, setActiveRuntime] = useState<RuntimeTab>('pandas');
  const [activeConnector, setActiveConnector] = useState<ConnectorTab>('snowflake');

  return (
    <div className="max-w-[1400px] mx-auto space-y-10 pb-12">

      {/* ── Hero ── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500/10 via-amber-500/5 to-yellow-500/10
                   border border-orange-400/30 px-8 py-10"
      >
        <GitBranch className="absolute bottom-4 right-4 w-32 h-32 text-orange-500 opacity-[0.04]" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-orange-500/15 flex items-center justify-center">
              <Radio className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-cargo-text">PDQ</h1>
              <p className="text-sm font-medium text-orange-500">Proactive Data Quality</p>
            </div>
          </div>

          <p className="text-cargo-muted max-w-2xl leading-relaxed mt-3">
            Embed data quality validation directly into your data pipelines. Use the{' '}
            <code className="px-1.5 py-0.5 bg-orange-500/10 text-orange-600 rounded text-sm font-mono">Artifi_ge_lib</code>{' '}
            SDK to validate DataFrames, database tables, and query results — in Pandas, Spark, or SQL — with
            full profiling, severity-based failure handling, and HTML report generation.
          </p>

          <div className="flex items-center gap-6 mt-5">
            <div className="flex items-center gap-2 text-sm text-cargo-muted">
              <FileSpreadsheet className="w-4 h-4 text-orange-500" />
              Pandas
            </div>
            <div className="flex items-center gap-2 text-sm text-cargo-muted">
              <Zap className="w-4 h-4 text-orange-500" />
              Spark
            </div>
            <div className="flex items-center gap-2 text-sm text-cargo-muted">
              <Database className="w-4 h-4 text-orange-500" />
              SQL
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── How It Works ── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        transition={{ delay: 0.1 }}
      >
        <h2 className="text-lg font-bold text-cargo-text mb-5 flex items-center gap-2">
          <Play className="w-5 h-5 text-orange-500" />
          How It Works
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <div key={s.step} className="relative bg-cargo-surface border border-cargo-border rounded-xl p-5">
              <div className="text-3xl font-bold text-orange-500/20 mb-2">{s.step}</div>
              <h3 className="text-sm font-bold text-cargo-text mb-1.5">{s.title}</h3>
              <p className="text-xs text-cargo-muted leading-relaxed">{s.description}</p>
              {i < steps.length - 1 && (
                <ChevronRight className="hidden md:block absolute top-1/2 -right-3 w-5 h-5 text-cargo-border -translate-y-1/2" />
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Features ── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        transition={{ delay: 0.15 }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="flex gap-4 bg-cargo-surface border border-cargo-border rounded-xl p-5"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-cargo-text mb-1">{f.title}</h3>
                  <p className="text-xs text-cargo-muted leading-relaxed">{f.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* ── Installation ── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        transition={{ delay: 0.2 }}
        className="space-y-4"
      >
        <h2 className="text-lg font-bold text-cargo-text flex items-center gap-2">
          <Package className="w-5 h-5 text-orange-500" />
          Installation
        </h2>

        <CodeBlock
          code="pip install Artifi_ge_lib"
          title="Terminal"
        />
      </motion.div>

      {/* ── Configuration ── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        transition={{ delay: 0.25 }}
        className="space-y-4"
      >
        <h2 className="text-lg font-bold text-cargo-text flex items-center gap-2">
          <Key className="w-5 h-5 text-orange-500" />
          Configuration
        </h2>

        <p className="text-sm text-cargo-muted">
          Create a <code className="px-1.5 py-0.5 bg-cargo-panel rounded text-cargo-text text-xs font-mono">pdq_config.json</code> with
          your DQ API endpoint and Okta credentials. Then initialize the consumer.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CodeBlock code={CONFIG_JSON} title="pdq_config.json" />
          <CodeBlock code={CONFIG_CODE} title="initialize.py" />
        </div>
      </motion.div>

      {/* ── SDK Usage — Runtime Tabs ── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        transition={{ delay: 0.3 }}
        className="space-y-4"
      >
        <h2 className="text-lg font-bold text-cargo-text flex items-center gap-2">
          <Terminal className="w-5 h-5 text-orange-500" />
          SDK Usage
        </h2>

        <p className="text-sm text-cargo-muted">
          The SDK auto-detects your runtime. Pass a Pandas DataFrame, Spark DataFrame, or table name with a SQL
          connection string. Validation and profiling run automatically.
        </p>

        {/* Runtime tabs */}
        <div className="flex gap-1 bg-cargo-surface border border-cargo-border rounded-lg p-1">
          {RUNTIME_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveRuntime(id)}
              className={`flex items-center gap-2 flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all
                ${activeRuntime === id
                  ? 'bg-orange-500 text-white shadow-sm'
                  : 'text-cargo-muted hover:text-cargo-text hover:bg-cargo-hover/50'
                }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        <CodeBlock
          code={RUNTIME_CODE[activeRuntime]}
          title={`${activeRuntime}_pipeline.py`}
        />

        {/* SQL connector sub-tabs */}
        {activeRuntime === 'sql' && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-cargo-text flex items-center gap-2">
              <Database className="w-4 h-4 text-orange-500" />
              Other SQL Connectors
            </h3>
            <div className="flex gap-1 bg-cargo-panel rounded-lg p-1 w-fit">
              {CONNECTOR_TABS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveConnector(id)}
                  className={`py-1.5 px-3 rounded-md text-xs font-medium transition-all
                    ${activeConnector === id
                      ? 'bg-cargo-accent text-white shadow-sm'
                      : 'text-cargo-muted hover:text-cargo-text'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {activeConnector !== 'snowflake' && (
              <CodeBlock code={CONNECTOR_CODE[activeConnector]} title={`${activeConnector}_connection.py`} />
            )}
          </div>
        )}
      </motion.div>

      {/* ── Results Structure ── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        transition={{ delay: 0.35 }}
        className="space-y-4"
      >
        <h2 className="text-lg font-bold text-cargo-text flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-orange-500" />
          Understanding Results
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-cargo-surface border border-cargo-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-cargo-text mb-2">Validation</h3>
            <ul className="text-xs text-cargo-muted space-y-1.5">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                <span><code className="text-cargo-text">results.success</code> — overall pass/fail</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                <span><code className="text-cargo-text">results.statistics</code> — evaluated, passed, failed counts</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                <span><code className="text-cargo-text">results.results</code> — per-rule details</span>
              </li>
            </ul>
          </div>

          <div className="bg-cargo-surface border border-cargo-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-cargo-text mb-2">Severity</h3>
            <ul className="text-xs text-cargo-muted space-y-1.5">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                <span><code className="text-cargo-text">get_failures_by_severity()</code> — High, Medium, Low</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                <span><code className="text-cargo-text">get_failed_row_indices()</code> — exact row numbers</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                <span>Use severity to gate pipelines or send alerts</span>
              </li>
            </ul>
          </div>

          <div className="bg-cargo-surface border border-cargo-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-cargo-text mb-2">Profiling</h3>
            <ul className="text-xs text-cargo-muted space-y-1.5">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                <span><code className="text-cargo-text">meta["profiling_metrics"]</code> — column stats</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                <span>Min, Max, Mean, Median, Null%, Type per column</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                <span>Auto-runs: freshness, outliers, constant detection</span>
              </li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* ── Supported Connectors ── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        transition={{ delay: 0.4 }}
        className="space-y-4"
      >
        <h2 className="text-lg font-bold text-cargo-text flex items-center gap-2">
          <Settings className="w-5 h-5 text-orange-500" />
          Supported Connectors
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {['Snowflake', 'PostgreSQL', 'MySQL', 'MSSQL', 'Oracle', 'Databricks'].map((name) => (
            <div
              key={name}
              className="bg-cargo-surface border border-cargo-border rounded-lg p-3 text-center"
            >
              <Database className="w-5 h-5 text-orange-500 mx-auto mb-1.5" />
              <p className="text-xs font-medium text-cargo-text">{name}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { name: 'Pandas DataFrame', desc: 'CSV, Parquet, Excel, JSON, SQL query results' },
            { name: 'Spark DataFrame', desc: 'S3, HDFS, Delta Lake, Iceberg, Hive tables' },
            { name: 'SQL Tables & Queries', desc: 'Direct table validation or custom SQL queries' },
          ].map((item) => (
            <div
              key={item.name}
              className="bg-cargo-surface border border-cargo-border rounded-lg p-4"
            >
              <p className="text-sm font-semibold text-cargo-text mb-1">{item.name}</p>
              <p className="text-xs text-cargo-muted">{item.desc}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default PDQPage;
